import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail,
  MapPin,
  Send,
  Linkedin,
  Twitter,
  Youtube,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import {
  Container,
  Section,
  Badge,
  Card,
  Button,
  OtpModal,
  PhoneInput,
  composePhoneNumber,
  DEFAULT_PHONE_VALUE,
  type PhoneValue,
} from '../components/ui';
import { PageHero, OurPurpose } from '../components/sections';
import { CONTACT_INFO, SOCIAL_LINKS, SERVICES } from '../constants';
import { sendOtp, validateOtpAndSubmit } from '../services/contactApi';

interface ContactFormState {
  name: string;
  workEmail: string;
  personalEmail: string;
  company: string;
  phone: PhoneValue;
  service: string;
  customService: string;
  message: string;
}

const INITIAL_FORM: ContactFormState = {
  name: '',
  workEmail: '',
  personalEmail: '',
  company: '',
  phone: DEFAULT_PHONE_VALUE,
  service: '',
  customService: '',
  message: '',
};

// Rendered above the auto-generated SERVICES list so they always appear on top.
const TOP_SERVICES = [
  { id: 'it-consultancy', title: 'IT Consultancy' },
  { id: 'product-development', title: 'Product Development' },
] as const;

function resolveServiceLabel(state: ContactFormState): string {
  if (state.service === 'other') {
    return state.customService.trim() || 'Other / Not Sure';
  }
  const top = TOP_SERVICES.find((s) => s.id === state.service);
  if (top) return top.title;
  const listed = SERVICES.find((s) => s.id === state.service);
  return listed?.title || state.service;
}

export const Contact: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<ContactFormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpVerifyError, setOtpVerifyError] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  useEffect(() => {
    const inquiry = searchParams.get('inquiry');

    if (inquiry === 'enterprise') {
      setFormData((prev) => ({
        ...prev,
        message:
          'I am interested in enterprise solutions for my organization. Please contact me to discuss our requirements.\n\n',
        service: 'other',
      }));
    }

    if (inquiry === 'support') {
      setFormData((prev) => ({
        ...prev,
        message: 'I need support with:\n\n',
        service: 'other',
      }));
    }

    if (inquiry === 'consultation') {
      setFormData((prev) => ({
        ...prev,
        message: 'I would like to schedule a consultation to discuss:\n\n',
        service: 'other',
      }));
    }
  }, [searchParams]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.workEmail.trim()) {
      newErrors.workEmail = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      newErrors.workEmail = 'Please enter a valid email address';
    }

    if (
      formData.personalEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalEmail)
    ) {
      newErrors.personalEmail = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }

    if (!formData.phone.number.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s-]{6,}$/.test(formData.phone.number.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service';
    } else if (formData.service === 'other' && !formData.customService.trim()) {
      newErrors.customService = 'Please describe the service you are looking for';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Please provide more details (at least 10 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await sendOtp({
        name: formData.name.trim(),
        email: formData.workEmail.trim(),
        require_work_email: true,
      });

      if (response.status === 'success') {
        setShowOtpModal(true);
      } else if (response.status_code === 400) {
        setErrors((prev) => ({
          ...prev,
          workEmail:
            response.message ||
            'Please use a work email address. Personal providers (gmail, hotmail, yahoo…) are not accepted.',
        }));
      } else if (response.status_code === 429) {
        const retryAfter = (response.data as { retry_after_seconds?: number } | null)?.retry_after_seconds;
        if (retryAfter) {
          setApiError(`Please wait ${retryAfter} seconds before requesting another OTP.`);
        } else {
          setApiError(response.message);
        }
      } else {
        setApiError(response.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpVerify = useCallback(
    async (otp: string) => {
      setOtpVerifyError('');
      setIsVerifyingOtp(true);

      try {
        const serviceName = resolveServiceLabel(formData);

        const response = await validateOtpAndSubmit({
          name: formData.name.trim(),
          work_email: formData.workEmail.trim(),
          personal_email: formData.personalEmail.trim() || 'na@gmail.com',
          company: formData.company.trim(),
          phone_number: composePhoneNumber(formData.phone),
          service_interested_in: serviceName,
          message: formData.message.trim(),
          otp,
        });

        if (response.status === 'success') {
          setShowOtpModal(false);
          setIsSubmitted(true);
        } else {
          setOtpVerifyError(response.message || 'Verification failed. Please try again.');
        }
      } finally {
        setIsVerifyingOtp(false);
      }
    },
    [formData],
  );

  const handleResendOtp = useCallback(async () => {
    const response = await sendOtp({
      name: formData.name.trim(),
      email: formData.workEmail.trim(),
      require_work_email: true,
    });

    if (response.status === 'success') {
      return { success: true };
    }
    if (response.status_code === 429) {
      const retryAfter = (response.data as { retry_after_seconds?: number } | null)?.retry_after_seconds;
      setOtpVerifyError(response.message);
      return { success: false, retryAfter: retryAfter || 60 };
    }
    setOtpVerifyError(response.message || 'Failed to resend OTP.');
    return { success: false };
  }, [formData.name, formData.workEmail]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // If the user picks a preset service, forget any lingering custom text
      // so it can't accidentally be submitted the next time they flip to "other".
      if (name === 'service' && value !== 'other') {
        next.customService = '';
      }
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (name === 'service' && errors.customService) {
      setErrors((prev) => ({ ...prev, customService: '' }));
    }
    if (apiError) setApiError('');
  };

  const handlePhoneChange = (value: PhoneValue) => {
    setFormData((prev) => ({ ...prev, phone: value }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: '' }));
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData(INITIAL_FORM);
    setErrors({});
    setApiError('');
    setOtpVerifyError('');
  };

  return (
    <>
      {/* OTP Verification Modal */}
      <OtpModal
        isOpen={showOtpModal}
        email={formData.workEmail}
        onClose={() => {
          setShowOtpModal(false);
          setOtpVerifyError('');
        }}
        onVerify={handleOtpVerify}
        onResend={handleResendOtp}
        verifyError={otpVerifyError}
        isVerifying={isVerifyingOtp}
      />

      {/* Hero Section */}
      <PageHero
        badge={
          <Badge className="mb-6 bg-white/10 text-white border border-white/20">
            Contact Us
          </Badge>
        }
        title="Let's Build Something Great Together"
        subtitle="Ready to start your project? Have questions about our services? We'd love to hear from you."
      />

      {/* Our Purpose */}
      <OurPurpose />

      {/* Contact Form Section */}
      <Section background="white" padding="lg">
        <Container>
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="heading-md text-navy-900 mb-6">Get in Touch</h2>
              <p className="text-body mb-8">
                Fill out the form and our team will get back to you within 24 hours.
                You can also reach us through the following channels.
              </p>

              <div className="space-y-6 mb-8">
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                    <Mail className="w-6 h-6 text-accent-600" />
                  </div>
                  <div>
                    <div className="text-sm text-navy-500">Email</div>
                    <div className="text-navy-900 font-medium group-hover:text-accent-600 transition-colors">
                      {CONTACT_INFO.email}
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-accent-600" />
                  </div>
                  <div>
                    <div className="text-sm text-navy-500">Address</div>
                    <div className="text-navy-900 font-medium">
                      {CONTACT_INFO.address}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-accent-600" />
                  </div>
                  <div>
                    <div className="text-sm text-navy-500">Business Hours</div>
                    <div className="text-navy-900 font-medium">
                      Mon - Fri: 9:00 AM - 6:00 PM IST
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <div className="text-sm text-navy-500 mb-3">Follow Us</div>
                <div className="flex gap-3">
                  <a
                    href={SOCIAL_LINKS.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-navy-100 flex items-center justify-center text-navy-600 hover:bg-accent-500 hover:text-white transition-all"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href={SOCIAL_LINKS.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-navy-100 flex items-center justify-center text-navy-600 hover:bg-accent-500 hover:text-white transition-all"
                    aria-label="X (Twitter)"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a
                    href={SOCIAL_LINKS.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-navy-100 flex items-center justify-center text-navy-600 hover:bg-accent-500 hover:text-white transition-all"
                    aria-label="YouTube"
                  >
                    <Youtube className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card padding="lg" className="bg-navy-50 border-0">
                {isSubmitted ? (
                  <motion.div
                    className="text-center py-12"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-navy-900 mb-2">Thank You!</h3>
                    <p className="text-navy-600 mb-6">
                      Your inquiry has been submitted successfully. Our team will get back to you
                      within 24 hours.
                    </p>
                    <Button variant="outline" onClick={resetForm}>
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {apiError && (
                      <motion.div
                        className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-600">{apiError}</p>
                      </motion.div>
                    )}

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-navy-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-lg border ${
                            errors.name
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all bg-white`}
                          placeholder="John Doe"
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.name}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-navy-700 mb-2">
                          Company *
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-lg border ${
                            errors.company
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all bg-white`}
                          placeholder="Company Inc."
                        />
                        {errors.company && (
                          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.company}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="workEmail" className="block text-sm font-medium text-navy-700 mb-2">
                          Work Email *
                        </label>
                        <input
                          type="email"
                          id="workEmail"
                          name="workEmail"
                          value={formData.workEmail}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-lg border ${
                            errors.workEmail
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all bg-white`}
                          placeholder="you@company.com"
                        />
                        <p className="mt-1 text-xs text-navy-400">
                          We'll send a verification code here. Personal domains (gmail, yahoo…) are
                          not accepted.
                        </p>
                        {errors.workEmail && (
                          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.workEmail}
                          </p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="personalEmail"
                          className="block text-sm font-medium text-navy-700 mb-2"
                        >
                          Personal Email{' '}
                          <span className="text-navy-400 font-normal">(optional)</span>
                        </label>
                        <input
                          type="email"
                          id="personalEmail"
                          name="personalEmail"
                          value={formData.personalEmail}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-lg border ${
                            errors.personalEmail
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all bg-white`}
                          placeholder="you@gmail.com"
                        />
                        {errors.personalEmail && (
                          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.personalEmail}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-navy-700 mb-2">
                          Phone Number *
                        </label>
                        <PhoneInput
                          id="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          hasError={Boolean(errors.phone)}
                        />
                        {errors.phone && (
                          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.phone}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="service" className="block text-sm font-medium text-navy-700 mb-2">
                          Service Interested In *
                        </label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-lg border ${
                            errors.service
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all bg-white`}
                        >
                          <option value="">Select a service</option>
                          {TOP_SERVICES.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.title}
                            </option>
                          ))}
                          {SERVICES.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.title}
                            </option>
                          ))}
                          <option value="other">Other / Not Sure</option>
                        </select>
                        {errors.service && (
                          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.service}
                          </p>
                        )}
                        {formData.service === 'other' && (
                          <div className="mt-2">
                            <input
                              type="text"
                              id="customService"
                              name="customService"
                              value={formData.customService}
                              onChange={handleChange}
                              autoFocus
                              className={`w-full px-4 py-3 rounded-lg border ${
                                errors.customService
                                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                              } focus:ring-2 outline-none transition-all bg-white`}
                              placeholder="Please describe the service you're looking for"
                            />
                            {errors.customService && (
                              <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {errors.customService}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-navy-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${
                          errors.message
                            ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                        } focus:ring-2 outline-none transition-all resize-none bg-white`}
                        placeholder="Tell us about your project or requirements..."
                      />
                      {errors.message && (
                        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full"
                      isLoading={isSubmitting}
                      rightIcon={<Send className="w-5 h-5" />}
                    >
                      Send Message
                    </Button>

                    <p className="text-xs text-navy-400 text-center">
                      We'll send a 4-digit verification code to your work email to confirm your
                      identity.
                    </p>
                  </form>
                )}
              </Card>
            </div>
          </div>
        </Container>
      </Section>

      {/* FAQ Section */}
      <Section background="mesh" padding="lg">
        <Container size="md">
          <div className="text-center mb-12">
            <Badge variant="accent" className="mb-4">
              FAQ
            </Badge>
            <h2 className="heading-lg text-navy-900">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'What is your typical response time?',
                a: 'We respond to all inquiries within 24 hours during business days. For urgent matters, please reach us via email.',
              },
              {
                q: 'Do you offer free consultations?',
                a: 'Yes! We offer a free initial consultation to understand your requirements and discuss how we can help.',
              },
              {
                q: 'What industries do you serve?',
                a: 'We serve clients across various industries including fintech, healthcare, e-commerce, education, and enterprise software.',
              },
              {
                q: 'Can you work with our existing team?',
                a: "Absolutely. We offer staff augmentation services where our experts integrate seamlessly with your team's workflow and processes.",
              },
            ].map((faq, index) => (
              <Card key={index} className="text-left">
                <h3 className="text-lg font-semibold text-navy-900 mb-2">{faq.q}</h3>
                <p className="text-navy-600">{faq.a}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
};
